import { filtersToQueryString as toQueryString } from '@/appdata/filters-to-query-string'
import { withPageSize } from '@/appdata/page-size'
import { api } from '@/api/client'
import { handleError } from '@/appdata/globals'
import type { FilterDescriptor } from '@progress/kendo-data-query'
import type { SettingElementInterface } from '@/types/items'

export interface PaginatedResponse<T> {
    metadata: {
        total: number | null
        start: number | null
        end: number | null
    }
    links: {
        next: string | null
        current: string | null
        previous: string | null
    }
    results: T[]
}

const emptyResponse = <T>(): PaginatedResponse<T> => ({
    metadata: { total: null, start: null, end: null },
    links: { next: null, current: null, previous: null },
    results: [] as T[],
})

const sortByName = (a: SettingElementInterface, b: SettingElementInterface): number => {
    if ((a.name ?? '') > (b.name ?? '')) return 1
    if ((a.name ?? '') < (b.name ?? '')) return -1
    return 0
}

export class ApiDataProvider {
    filtersToQueryString(filters: FilterDescriptor[]): string {
        return toQueryString(filters)
    }

    fetchElements(
        systemUuid: string,
        alias: string,
        url?: string | null,
        filters?: FilterDescriptor[],
        limit?: number | null,
    ): Promise<PaginatedResponse<SettingElementInterface>> {
        let requestUrl: string
        if (url) {
            requestUrl = url
        } else {
            requestUrl = '/systems/' + systemUuid + '/' + alias
            if (filters && filters.length > 0) {
                requestUrl = requestUrl + '?' + this.filtersToQueryString(filters)
            }
            // Only a url built here takes the page size. A given url is a page link from the API,
            // which already carries the limit of the request that produced it (spec 2026-10-16).
            requestUrl = withPageSize(requestUrl, limit)
        }

        return api.get(requestUrl)
            .then((response) => {
                let results: SettingElementInterface[]
                if (Array.isArray(response.data?.results)) {
                    results = [...response.data.results].sort(sortByName)
                } else if (Array.isArray(response.data)) {
                    results = [...response.data].sort(sortByName)
                } else {
                    results = []
                }

                return {
                    metadata: response.data?.metadata ?? { total: null, start: null, end: null },
                    links: response.data?.links ?? { next: null, current: null, previous: null },
                    results,
                }
            })
            .catch((error) => {
                handleError(error)
                return emptyResponse<SettingElementInterface>()
            })
    }

    fetchElementDetail(uuid: string): Promise<SettingElementInterface | null> {
        if (!uuid) {
            return Promise.resolve(null)
        }

        return api.get('/elements/' + uuid + '/one')
            .then((response) => response.data as SettingElementInterface)
            .catch((error) => {
                handleError(error)
                return null
            })
    }
}
